// Pure rules behind the Provision screen: which staged sources a run holds, which connections
// and target groups may receive each one, and how an assignment map changes when the user picks
// a connection or a whole group. No React, no API — every rule here is unit-tested on its own.
import { engineKey, isTdmTarget } from '../tdmUtils';

/**
 * The sources a provision matches targets against: the subset's own rows, or — for a subset
 * imported from staging, which has none — the sources its subset.json described (slug + engine).
 */
export const subsetSources = (subset) => {
  if (!subset) return [];
  if (Array.isArray(subset.sources) && subset.sources.length) return subset.sources;
  return Array.isArray(subset.importedSources) ? subset.importedSources : [];
};

const bySlug = (sources) => {
  const map = {};
  (sources || []).forEach((s) => { if (s && s.slug) map[s.slug] = s; });
  return map;
};

const fromSource = (s, rows) => ({
  slug: s.slug,
  dataSourceId: s.dataSourceId,
  dataSourceName: s.dataSourceName,
  engine: s.engine,
  parent: Boolean(s.parent),
  rows: rows == null ? null : rows,
});

/**
 * The staged sources of a run — "connectors" — joined to the subset's sources by slug. The run's
 * `totals.perSource` is authoritative (it is what was actually staged, with row counts); a slug
 * the subset no longer has is kept as an orphan so the user sees it rather than losing it. A run
 * without totals falls back to every subset source.
 */
export const connectorsForRun = (run, sources) => {
  const index = bySlug(sources);
  const perSource = run && run.totals && Array.isArray(run.totals.perSource) ? run.totals.perSource : [];
  if (perSource.length === 0) return (sources || []).filter((s) => s && s.slug).map((s) => fromSource(s, null));
  return perSource.map((p) => {
    const s = index[p.slug];
    if (s) return fromSource(s, p.rows);
    return { slug: p.slug, dataSourceId: null, dataSourceName: p.slug, engine: null, parent: false, rows: p.rows == null ? null : p.rows, orphan: true };
  });
};

/** The assignment map a previous provision run implies — only for slugs that are still connectors. */
export const assignmentsFromRun = (provisionRun, connectors) => {
  const slugs = new Set((connectors || []).map((c) => c.slug));
  const out = {};
  ((provisionRun && provisionRun.targets) || []).forEach((t) => {
    if (!slugs.has(t.sourceSlug)) return;
    const a = {
      dataSourceId: t.dataSourceId,
      dataSourceName: t.dataSourceName,
      engine: t.engine,
    };
    if (t.targetGroupId != null) {
      a.targetGroupId = t.targetGroupId;
      a.targetGroupName = t.targetGroupName;
    }
    out[t.sourceSlug] = a;
  });
  return out;
};

/** An assignment built from a connection row (`AppContext.connections` shape). */
export const targetFromConnection = (c) => ({
  dataSourceId: c.id,
  dataSourceName: c.name,
  engine: c.type || c.engine,
});

const engineSetFor = (connectors, selectedSlug) => {
  const list = connectors || [];
  const selected = selectedSlug ? list.find((c) => c.slug === selectedSlug) : null;
  const engines = selected ? [selected.engine] : list.map((c) => c.engine);
  return new Set(engines.filter(Boolean).map(engineKey));
};

const connEngine = (c) => engineKey(c && (c.type || c.engine));

/**
 * What the "Add target" lists offer: connections TDM can write to whose engine matches the
 * selected connector (or any connector when none is selected), and target groups with at least one
 * member of such an engine. Read-only connections never appear — provisioning writes rows.
 */
export const eligibleTargets = ({ connectors, selectedSlug, connections, targetGroups }) => {
  const keys = engineSetFor(connectors, selectedSlug);
  const conns = (connections || []).filter((c) => isTdmTarget(c) && keys.has(connEngine(c)));
  const groups = (targetGroups || []).filter((g) => (g.members || []).some((m) => keys.has(connEngine(m))));
  return { connections: conns, groups };
};

/**
 * Assign one connection. The selected connector takes it (replacing whatever it had); with no
 * selection it goes to the first connector of that engine that has nothing yet. No candidate →
 * the map is returned unchanged.
 */
export const assignConnection = (assignments, connectors, selectedSlug, conn) => {
  const current = assignments || {};
  const list = connectors || [];
  let slug = selectedSlug && list.some((c) => c.slug === selectedSlug) ? selectedSlug : null;
  if (!slug) {
    const key = connEngine(conn);
    const free = list.find((c) => !current[c.slug] && engineKey(c.engine) === key);
    slug = free ? free.slug : null;
  }
  if (!slug) return current;
  return { ...current, [slug]: targetFromConnection(conn) };
};

/**
 * Assign a whole target group: each connector takes the group's one writable member of its engine.
 * A connector with two candidates is ambiguous — nothing is assigned and the error names it; a
 * connector with none is left as it was. A matched connector keeps its schema override and loses
 * its previous single target.
 */
export const assignTargetGroup = (assignments, connectors, group) => {
  const current = assignments || {};
  const members = ((group && group.members) || []).filter((m) => !m.readOnly);
  const matches = (connectors || []).map((c) => ({ c, found: members.filter((m) => engineKey(c.engine) && connEngine(m) === engineKey(c.engine)) }));
  const ambiguous = matches.find((m) => m.found.length > 1);
  if (ambiguous) {
    const eng = engineKey(ambiguous.c.engine).toUpperCase();
    return { assignments: current, error: `Target group ${group.name} has two ${eng} targets for ${ambiguous.c.dataSourceName}; pick one directly` };
  }
  const next = { ...current };
  matches.forEach(({ c, found }) => {
    if (found.length !== 1) return;
    const m = found[0];
    next[c.slug] = {
      dataSourceId: m.dataSourceId,
      dataSourceName: m.dataSourceName,
      engine: m.engine,
      targetGroupId: group.id,
      targetGroupName: group.name,
    };
  });
  return { assignments: next, error: null };
};

/** Provisionable runs, newest first (ids are monotonic). */
export const provisionableRuns = (runs) => (runs || []).filter((r) => r && r.provisionable).sort((a, b) => Number(b.id) - Number(a.id));

/** The run the picker defaults to. */
export const newestProvisionable = (runs) => provisionableRuns(runs)[0] || null;
